import React, { useState, useEffect, useMemo } from "react";
import { getUsers, getInvitations, getChannels, createUser, inviteUser, updateUser, deleteUser, resendInvitation, revokeInvitation } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash, PaperPlaneTilt, X, User, Clock, CheckCircle, Copy, UserPlus } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const ALL_ROLES = ["admin", "manager", "editor", "viewer"];
const MANAGER_ROLES = ["manager", "editor", "viewer"];
const STATUSES = ["active", "suspended", "deactivated"];

export default function Users() {
  const { user: currentUser, hasPermission } = useAuth();
  const isManager = currentUser?.role === "manager";
  const ROLES = isManager ? MANAGER_ROLES : ALL_ROLES;
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [formData, setFormData] = useState({ email: "", name: "", role: "viewer", assigned_channels: [], message: "" });
  const [createFormData, setCreateFormData] = useState({ email: "", name: "", password: "", role: "viewer", assigned_channels: [] });

  const pendingInvitations = useMemo(() => invitations.filter(i => i.status === "pending"), [invitations]);
  const [editFormData, setEditFormData] = useState({ role: "", status: "", assigned_channels: [] });

  const fetchData = async () => {
    try {
      const [usersRes, invitesRes, channelsRes] = await Promise.all([getUsers(), getInvitations(), getChannels()]);
      setUsers(usersRes.data);
      setInvitations(invitesRes.data);
      setChannels(channelsRes.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await inviteUser(formData);
      toast.success("Invitation sent!");
      setInviteLink(response.data.invite_link);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(createFormData);
      toast.success("User created successfully!");
      setCreateDialogOpen(false);
      setCreateFormData({ email: "", name: "", password: "", role: "viewer", assigned_channels: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setEditFormData({ role: user.role, status: user.status, assigned_channels: user.assigned_channels || [] });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser(editingUser.id, editFormData);
      toast.success("User updated");
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser(deletingUser.id);
      toast.success("User deleted");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResendInvite = async (id) => {
    try {
      const response = await resendInvitation(id);
      toast.success("Invitation resent");
      setInviteLink(response.data.invite_link);
    } catch (err) {
      toast.error("Failed to resend");
    }
  };

  const handleRevokeInvite = async (id) => {
    try {
      await revokeInvitation(id);
      toast.success("Invitation revoked");
      fetchData();
    } catch (err) {
      toast.error("Failed to revoke");
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success("Link copied!");
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="surface-card p-6"><div className="skeleton h-16 w-full" /></div>)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">User Management</h1>
          <p className="text-gray-400 text-sm">{users.length} users, {invitations.filter(i => i.status === "pending").length} pending invites</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setCreateDialogOpen(true); setCreateFormData({ email: "", name: "", password: "", role: "viewer", assigned_channels: [] }); }} className="btn-primary" data-testid="create-user-btn">
            <UserPlus size={18} className="mr-2" />Create User
          </Button>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="invitations" className="data-[state=active]:bg-blue-600">Invitations ({invitations.filter(i => i.status === "pending").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="surface-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
                  <th className="text-left p-4 font-medium">User</th>
                  <th className="text-left p-4 font-medium">Role</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Last Login</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, idx) => (
                  <tr key={user.id} className={`table-row ${idx % 2 === 1 ? "table-row-even" : ""}`} data-testid={`user-row-${user.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <User size={18} className="text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-sm text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`badge ${user.role === "owner" ? "bg-purple-500/20 text-purple-400 border-purple-500/30" : user.role === "admin" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "bg-gray-500/20 text-gray-400 border-gray-500/30"}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`badge ${user.status === "active" ? "bg-green-500/20 text-green-400 border-green-500/30" : user.status === "suspended" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400 text-sm">{user.last_login?.split("T")[0] || "Never"}</td>
                    <td className="p-4">
                      {user.role !== "owner" && user.id !== currentUser?.id && (
                        <div className="flex gap-1">
                          {(!isManager || (user.role !== "admin" && user.role !== "owner")) && (
                            <button onClick={() => handleOpenEdit(user)} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"><Pencil size={16} /></button>
                          )}
                          {!isManager && (
                            <button onClick={() => { setDeletingUser(user); setDeleteDialogOpen(true); }} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" data-testid={`delete-user-${user.id}`}><Trash size={16} /></button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="invitations">
          <div className="space-y-3">
            {pendingInvitations.map((invite) => (
              <div key={invite.id} className="surface-card p-4 flex items-center justify-between" data-testid={`invite-row-${invite.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Clock size={18} className="text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{invite.name}</p>
                    <p className="text-sm text-gray-400">{invite.email} • {invite.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => handleResendInvite(invite.id)} variant="outline" size="sm" className="btn-secondary">
                    <PaperPlaneTilt size={14} className="mr-1" />Resend
                  </Button>
                  <Button onClick={() => handleRevokeInvite(invite.id)} variant="outline" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <X size={14} className="mr-1" />Revoke
                  </Button>
                </div>
              </div>
            ))}
            {invitations.filter(i => i.status === "pending").length === 0 && (
              <div className="text-center py-12 text-gray-500">No pending invitations</div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Create New User</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Name</Label>
              <Input value={createFormData.name} onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" required data-testid="create-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input type="email" value={createFormData.email} onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" required data-testid="create-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password</Label>
              <Input type="password" value={createFormData.password} onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })} className="bg-white/5 border-white/10 text-white" required minLength={6} data-testid="create-user-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Role</Label>
              <Select value={createFormData.role} onValueChange={(v) => setCreateFormData({ ...createFormData, role: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">{ROLES.map(r => <SelectItem key={r} value={r} className="text-white capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(createFormData.role === "manager" || createFormData.role === "editor") && (
              <div className="space-y-2">
                <Label className="text-gray-300">Assign Channels</Label>
                <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg bg-white/5 border border-white/10">
                  {channels.map(ch => (
                    <label key={ch.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-white">
                      <input type="checkbox" checked={(createFormData.assigned_channels || []).includes(ch.id)} onChange={(e) => {
                        if (e.target.checked) {
                          setCreateFormData({ ...createFormData, assigned_channels: [...(createFormData.assigned_channels || []), ch.id] });
                        } else {
                          setCreateFormData({ ...createFormData, assigned_channels: (createFormData.assigned_channels || []).filter(id => id !== ch.id) });
                        }
                      }} className="rounded" />
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ch.color }} />
                      {ch.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="create-user-submit">Create User</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Invite Team Member</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                <p className="text-green-400 font-medium">Invitation Sent!</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Invitation Link</Label>
                <div className="flex gap-2">
                  <Input value={inviteLink} readOnly className="bg-white/5 border-white/10 text-white text-xs" />
                  <Button onClick={copyInviteLink} variant="outline" className="btn-secondary"><Copy size={16} /></Button>
                </div>
                <p className="text-xs text-gray-500">Share this link with the invitee. It expires in 7 days.</p>
              </div>
              <Button onClick={() => { setInviteDialogOpen(false); setInviteLink(""); }} className="w-full btn-primary">Done</Button>
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2"><Label className="text-gray-300">Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" required data-testid="invite-email-input" /></div>
              <div className="space-y-2"><Label className="text-gray-300">Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" required data-testid="invite-name-input" /></div>
              <div className="space-y-2"><Label className="text-gray-300">Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">{ROLES.map(r => <SelectItem key={r} value={r} className="text-white capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(formData.role === "manager" || formData.role === "editor") && (
                <div className="space-y-2"><Label className="text-gray-300">Assign Channels</Label>
                  <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg bg-white/5 border border-white/10">
                    {channels.map(ch => (
                      <label key={ch.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-white">
                        <input type="checkbox" checked={formData.assigned_channels.includes(ch.id)} onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, assigned_channels: [...formData.assigned_channels, ch.id] });
                          } else {
                            setFormData({ ...formData, assigned_channels: formData.assigned_channels.filter(id => id !== ch.id) });
                          }
                        }} className="rounded" />
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ch.color }} />
                        {ch.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2"><Label className="text-gray-300">Message (optional)</Label><Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-white/5 border-white/10 text-white min-h-[60px]" placeholder="Add a personal message..." /></div>
              <div className="flex gap-3 pt-4"><Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button><Button type="submit" className="flex-1 btn-primary" data-testid="invite-submit-btn">Send Invitation</Button></div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Edit User - {editingUser?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300">Role</Label>
              <Select value={editFormData.role} onValueChange={(v) => setEditFormData({ ...editFormData, role: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">{ROLES.map(r => <SelectItem key={r} value={r} className="text-white capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="text-gray-300">Status</Label>
              <Select value={editFormData.status} onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">{STATUSES.map(s => <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4"><Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button><Button type="submit" className="flex-1 btn-primary">Save Changes</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Delete User?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">This will permanently remove "{deletingUser?.name}" from the system.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
